// callSessionStore.js — the SINGLE in-memory source of truth for the "during a call" panel (PP-0).
//
// Joey (Nov 3 2026): during a call the blue side menu tucks away and the phone panel takes its place on
// the left; a "Menu" button brings the side menu back WITHOUT ending the call; the account page stays
// fully usable (all tabs, every card). This store holds the one active-call session so the shell
// (Dashboard) and the panel — rendered in different parts of the tree — always agree. React's
// useSyncExternalStore keeps every subscriber in sync (same pattern as presenceStore.js).
//
// Ephemeral + in-tab: a call session is NOT server-persisted here. The call OUTCOME is still logged
// exactly once through the existing disposition path (PhoneProvider.openManualLog) — this store never
// counts a call and never writes to the backend. `startedAt` is a timestamp the panel renders elapsed
// time from; no timer is stored.
import { useSyncExternalStore } from 'react';

const EMPTY = {
  active: false,
  entityType: '',   // e.g. 'collections_account'
  entityId: '',
  number: '',
  startedAt: 0,     // epoch ms when the call began (panel renders elapsed from this)
  onHold: false,
  menuOpen: false,  // Menu button brings the side menu back WITHOUT ending the call
  onLogged: null,   // optional account-page refresh callback, run after the outcome is logged
};

let snapshot = EMPTY;
const listeners = new Set();

function emit() {
  listeners.forEach((l) => { try { l(); } catch { /* ignore */ } });
}

// Begin a call session (opens the panel). Replaces any prior session — one active call per tab.
export function startCall({ entityType, entityId, number, onLogged } = {}) {
  snapshot = {
    active: true,
    entityType: entityType || '',
    entityId: entityId || '',
    number: number || '',
    startedAt: Date.now(),
    onHold: false,
    menuOpen: false,
    onLogged: typeof onLogged === 'function' ? onLogged : null,
  };
  emit();
}

// End the call session (closes the panel). The outcome logging is handled separately by the caller
// via the existing disposition path — this only clears the session.
export function endCall() {
  if (!snapshot.active) return;
  snapshot = EMPTY;
  emit();
}

export function toggleMenu() {
  if (!snapshot.active) return;
  snapshot = { ...snapshot, menuOpen: !snapshot.menuOpen };
  emit();
}

export function setMenuOpen(open) {
  if (!snapshot.active || snapshot.menuOpen === !!open) return;
  snapshot = { ...snapshot, menuOpen: !!open };
  emit();
}

export function setHold(onHold) {
  if (!snapshot.active || snapshot.onHold === !!onHold) return;
  snapshot = { ...snapshot, onHold: !!onHold };
  emit();
}

// Clear on user switch (logout/login) — the store is a module-level singleton and this app's
// logout/login are SPA-only (no reload), so without this a second employee could inherit a stale panel.
export function resetCallSession() {
  snapshot = EMPTY;
  emit();
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return snapshot;
}

export function useCallSession() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
